import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Calendar, 
  Clock, 
  Truck, 
  Package, 
  Plus, 
  RefreshCw, 
  Bell, 
  MapPin,
  User,
  CheckCircle,
  XCircle,
  PlayCircle,
  Edit,
  Trash2,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  agendado: { label: 'Agendado', color: 'bg-blue-100 text-blue-800', icon: Calendar },
  confirmado: { label: 'Confirmado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  em_andamento: { label: 'Em Andamento', color: 'bg-yellow-100 text-yellow-800', icon: PlayCircle },
  concluido: { label: 'Concluído', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle }
};

export default function AgendamentosPage() {
  const [agendamentos, setAgendamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAgendamento, setEditingAgendamento] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const [formData, setFormData] = useState({
    cliente_id: '',
    tipo: 'coleta',
    data_agendada: '',
    hora_agendada: '',
    endereco: '',
    observacoes: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [agendamentosRes, clientesRes] = await Promise.all([
        api.get(`/agendamentos${filterStatus !== 'all' ? `?status=${filterStatus}` : ''}`),
        api.get('/clientes')
      ]);
      setAgendamentos(agendamentosRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setEditingAgendamento(null);
    setFormData({
      cliente_id: '',
      tipo: 'coleta',
      data_agendada: '',
      hora_agendada: '',
      endereco: '',
      observacoes: ''
    });
    setShowDialog(true);
  };

  const openEditDialog = (agendamento) => {
    setEditingAgendamento(agendamento);
    setFormData({
      cliente_id: agendamento.cliente_id,
      tipo: agendamento.tipo,
      data_agendada: agendamento.data_agendada,
      hora_agendada: agendamento.hora_agendada,
      endereco: agendamento.endereco || '',
      observacoes: agendamento.observacoes || ''
    });
    setShowDialog(true);
  };

  const handleClienteChange = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    setFormData(prev => ({
      ...prev,
      cliente_id: clienteId,
      endereco: cliente?.endereco || prev.endereco
    }));
  };

  const handleSubmit = async () => {
    if (!formData.cliente_id || !formData.data_agendada || !formData.hora_agendada) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      if (editingAgendamento) {
        await api.put(`/agendamentos/${editingAgendamento.id}`, formData);
        toast.success('Agendamento atualizado!');
      } else {
        await api.post('/agendamentos', formData);
        toast.success('Agendamento criado! Notificação enviada ao cliente.');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar agendamento');
    }
  };

  const handleStatusChange = async (agendamentoId, novoStatus) => {
    try {
      const response = await api.put(`/agendamentos/${agendamentoId}/status?novo_status=${novoStatus}`);
      toast.success('Status atualizado!');
      if (response.data.whatsapp_enviado) {
        toast.success('WhatsApp enviado ao cliente!');
      }
      if (response.data.email_enviado) {
        toast.success('Email enviado ao cliente!');
      }
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleNotificar = async (agendamentoId) => {
    try {
      const response = await api.post(`/agendamentos/${agendamentoId}/notificar`);
      if (response.data.notifications.whatsapp) {
        toast.success('WhatsApp enviado!');
      }
      if (response.data.notifications.email) {
        toast.success('Email enviado!');
      }
      fetchData();
    } catch (error) {
      toast.error('Erro ao enviar notificação');
    }
  };

  const handleDelete = async (agendamentoId) => {
    if (!window.confirm('Deseja excluir este agendamento?')) return;
    
    try {
      await api.delete(`/agendamentos/${agendamentoId}`);
      toast.success('Agendamento excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir agendamento');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Group by date
  const groupedByDate = agendamentos.reduce((acc, ag) => {
    const date = ag.data_agendada;
    if (!acc[date]) acc[date] = [];
    acc[date].push(ag);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="agendamentos-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Agendamentos</h1>
          <p className="text-slate-500">Gerencie coletas e entregas de materiais</p>
        </div>
        <div className="flex gap-2">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="filter-status">
              <SelectValue placeholder="Filtrar status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="agendado">Agendados</SelectItem>
              <SelectItem value="confirmado">Confirmados</SelectItem>
              <SelectItem value="em_andamento">Em Andamento</SelectItem>
              <SelectItem value="concluido">Concluídos</SelectItem>
              <SelectItem value="cancelado">Cancelados</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" title="Atualizar lista">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="new-agendamento-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Agendamento
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const count = agendamentos.filter(a => a.status === status).length;
          const Icon = config.icon;
          return (
            <div 
              key={status} 
              className={`p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${filterStatus === status ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setFilterStatus(status)}
            >
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-slate-600" />
                <span className="text-2xl font-bold text-slate-900">{count}</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">{config.label}</p>
            </div>
          );
        })}
      </div>

      {/* Agendamentos List */}
      {Object.keys(groupedByDate).length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-12 text-center">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nenhum agendamento encontrado</p>
          <Button onClick={openNewDialog} className="mt-4 bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Criar Primeiro Agendamento
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByDate).sort().map(([date, items]) => (
            <div key={date} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  {formatDate(date)}
                  <span className="text-sm text-slate-500 font-normal">({items.length} agendamento{items.length > 1 ? 's' : ''})</span>
                </h3>
              </div>
              <div className="divide-y divide-slate-100">
                {items.sort((a, b) => a.hora_agendada.localeCompare(b.hora_agendada)).map((ag) => {
                  const statusConfig = STATUS_CONFIG[ag.status] || STATUS_CONFIG.agendado;
                  const StatusIcon = statusConfig.icon;
                  
                  return (
                    <div key={ag.id} className="p-4 hover:bg-slate-50" data-testid={`agendamento-item-${ag.id}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${statusConfig.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${ag.tipo === 'coleta' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'}`}>
                              {ag.tipo === 'coleta' ? <Truck className="w-3 h-3 inline mr-1" /> : <Package className="w-3 h-3 inline mr-1" />}
                              {ag.tipo === 'coleta' ? 'Coleta' : 'Entrega'}
                            </span>
                            <span className="text-sm font-medium text-slate-900 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {ag.hora_agendada}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-slate-800">{ag.cliente?.nome || 'Cliente'}</span>
                            {ag.cliente?.telefone && (
                              <span className="text-sm text-slate-500 flex items-center gap-1">
                                <MessageCircle className="w-3 h-3" />
                                {ag.cliente.telefone}
                              </span>
                            )}
                          </div>
                          
                          {ag.endereco && (
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <MapPin className="w-4 h-4" />
                              {ag.endereco}
                            </div>
                          )}
                          
                          {ag.observacoes && (
                            <p className="text-sm text-slate-500 mt-1 italic">"{ag.observacoes}"</p>
                          )}
                          
                          {ag.notificacao_enviada && (
                            <span className="text-xs text-green-600 flex items-center gap-1 mt-1">
                              <Bell className="w-3 h-3" /> Notificação enviada
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-col gap-2 ml-4">
                          {ag.status !== 'concluido' && ag.status !== 'cancelado' && (
                            <>
                              <Select 
                                value={ag.status} 
                                onValueChange={(value) => handleStatusChange(ag.id, value)}
                              >
                                <SelectTrigger className="w-36 h-8 text-xs" data-testid={`status-select-${ag.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="agendado">Agendado</SelectItem>
                                  <SelectItem value="confirmado">Confirmado</SelectItem>
                                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                                  <SelectItem value="concluido">Concluído</SelectItem>
                                  <SelectItem value="cancelado">Cancelado</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleNotificar(ag.id)}
                                title="Enviar lembrete por WhatsApp/Email"
                                data-testid={`notificar-btn-${ag.id}`}
                              >
                                <Bell className="w-3 h-3 mr-1" />
                                Notificar
                              </Button>
                            </>
                          )}
                          
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => openEditDialog(ag)}
                              title="Editar agendamento"
                              data-testid={`edit-btn-${ag.id}`}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDelete(ag.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Excluir agendamento"
                              data-testid={`delete-btn-${ag.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              {editingAgendamento ? 'Editar Agendamento' : 'Novo Agendamento'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cliente">Cliente *</Label>
              <Select value={formData.cliente_id || undefined} onValueChange={handleClienteChange}>
                <SelectTrigger data-testid="cliente-select">
                  <SelectValue placeholder="Selecione o cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map(cliente => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome} {cliente.telefone && `- ${cliente.telefone}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo *</Label>
              <Select value={formData.tipo || undefined} onValueChange={(v) => setFormData(prev => ({ ...prev, tipo: v }))}>
                <SelectTrigger data-testid="tipo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coleta">
                    <span className="flex items-center gap-2">
                      <Truck className="w-4 h-4" /> Coleta
                    </span>
                  </SelectItem>
                  <SelectItem value="entrega">
                    <span className="flex items-center gap-2">
                      <Package className="w-4 h-4" /> Entrega
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data">Data *</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data_agendada}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_agendada: e.target.value }))}
                  data-testid="data-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hora">Hora *</Label>
                <Input
                  id="hora"
                  type="time"
                  value={formData.hora_agendada}
                  onChange={(e) => setFormData(prev => ({ ...prev, hora_agendada: e.target.value }))}
                  data-testid="hora-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="endereco">Endereço</Label>
              <Input
                id="endereco"
                value={formData.endereco}
                onChange={(e) => setFormData(prev => ({ ...prev, endereco: e.target.value }))}
                placeholder="Endereço para coleta/entrega"
                data-testid="endereco-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                placeholder="Observações adicionais..."
                rows={3}
                data-testid="observacoes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button 
              type="submit"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="submit-agendamento-btn"
            >
              {editingAgendamento ? 'Salvar Alterações' : 'Criar Agendamento'}
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
